import { Routes } from '@angular/router';
import { ALLOWS_SESSION_WITHOUT_ACCESS, AuthGuard } from './guards/auth.guard';
import { UnsavedChangesGuard } from './guards/unsaved-changes.guard';
import { AdminEnabledGuard } from './guards/admin-enabled.guard';
import { AdminRoleGuard } from './guards/admin-role.guard';
import { LogbookLoginEnabledGuard } from './guards/logbook-login-enabled.guard';
import { HotelGuard } from './guards/hotel.guard';
import { DashboardEditDesktopGuard } from './guards/dashboard-edit-desktop.guard';
import { DashboardEditSessionStore } from './modules/dashboards/dashboard-builder/dashboard-edit-session.store';
import { DashboardEditWidgetDataService } from './modules/dashboards/dashboard-builder/dashboard-edit-widget-data.service';

function dashboardEditorChildren(): Routes {
  return [
    {
      path: '',
      loadComponent: () =>
        import(
          './modules/dashboards/dashboard-builder/grid-editor/grid-editor-surface.component'
        ).then(m => m.GridEditorSurfaceComponent),
    },
    {
      path: 'add-widget',
      loadComponent: () =>
        import(
          './modules/dashboards/dashboard-builder/add-widget/add-widget-surface.component'
        ).then(m => m.AddWidgetSurfaceComponent),
    },
    {
      path: 'edit-widget/:widgetLocalId',
      loadComponent: () =>
        import(
          './modules/dashboards/dashboard-builder/edit-widget/edit-widget-surface.component'
        ).then(m => m.EditWidgetSurfaceComponent),
    },
  ];
}

const baseRoutes: Routes = [
  {
    path: 'auth/login',
    loadComponent: () =>
      import('./modules/authentication/login/login.component').then(
        m => m.LoginComponent
      ),
  },
  {
    path: 'auth/logout',
    redirectTo: 'auth/login',
  },
  {
    path: 'auth/ship-login',
    loadComponent: () =>
      import('./modules/authentication/ship-login/ship-login.component').then(
        m => m.ShipLoginComponent
      ),
  },
  {
    path: 'auth/callback',
    loadComponent: () =>
      import('./modules/authentication/callback/callback.component').then(
        m => m.CallbackComponent
      ),
  },
  {
    path: 'home',
    loadComponent: () =>
      import('./modules/home/home.component').then(m => m.HomeComponent),
    canActivate: [AuthGuard],
  },
  {
    path: 'preview/:previewId',
    loadComponent: () =>
      import('./modules/checklist/checklist.component').then(
        m => m.ChecklistComponent
      ),
    canActivate: [AuthGuard],
    data: { [ALLOWS_SESSION_WITHOUT_ACCESS]: true },
  },
  {
    path: 'checklist/preview/:templateId',
    loadComponent: () =>
      import('./modules/checklist/checklist.component').then(
        m => m.ChecklistComponent
      ),
    canActivate: [AuthGuard],
  },
  {
    path: 'checklist/:id',
    loadComponent: () =>
      import('./modules/checklist/checklist.component').then(
        m => m.ChecklistComponent
      ),
    canActivate: [AuthGuard],
  },
  {
    path: 'create-new',
    loadComponent: () =>
      import('./modules/checklist/new-checklist.component').then(
        m => m.NewChecklistComponent
      ),
    canActivate: [AuthGuard],
  },
  {
    path: 'filled-checklists',
    loadComponent: () =>
      import(
        './modules/checklist/filled-checklists/filled-checklists.component'
      ).then(m => m.FilledChecklistsComponent),
    canActivate: [AuthGuard],
  },
  {
    path: 'dashboards',
    canActivate: [AuthGuard, HotelGuard],
    loadComponent: () =>
      import(
        './modules/dashboards/dashboard-viewer/dashboard-viewer.component'
      ).then(m => m.DashboardViewerComponent),
  },
  {
    path: 'dashboards/new',
    canActivate: [
      AuthGuard,
      HotelGuard,
      AdminRoleGuard,
      DashboardEditDesktopGuard,
    ],
    canDeactivate: [UnsavedChangesGuard],
    data: { newDashboard: true },
    providers: [DashboardEditSessionStore, DashboardEditWidgetDataService],
    loadComponent: () =>
      import(
        './modules/dashboards/dashboard-builder/dashboard-edit-shell.component'
      ).then(m => m.DashboardEditShellComponent),
    children: dashboardEditorChildren(),
  },
  {
    path: 'dashboards/:id/edit',
    canActivate: [
      AuthGuard,
      HotelGuard,
      AdminRoleGuard,
      DashboardEditDesktopGuard,
    ],
    canDeactivate: [UnsavedChangesGuard],
    providers: [DashboardEditSessionStore, DashboardEditWidgetDataService],
    loadComponent: () =>
      import(
        './modules/dashboards/dashboard-builder/dashboard-edit-shell.component'
      ).then(m => m.DashboardEditShellComponent),
    children: dashboardEditorChildren(),
  },
  {
    path: 'dashboards/:id/alert-resolver',
    canActivate: [AuthGuard, HotelGuard, AdminRoleGuard],
    loadComponent: () =>
      import(
        './modules/dashboards/alert-resolver/alert-resolver.component'
      ).then(m => m.AlertResolverComponent),
  },
  {
    path: 'dashboards/:id',
    canActivate: [AuthGuard, HotelGuard],
    loadComponent: () =>
      import(
        './modules/dashboards/dashboard-viewer/dashboard-viewer.component'
      ).then(m => m.DashboardViewerComponent),
  },
  {
    path: 'account',
    loadComponent: () =>
      import('./modules/account/account.component').then(
        m => m.AccountComponent
      ),
    canActivate: [AuthGuard],
  },
  { path: 'auth/error', redirectTo: 'auth/login', pathMatch: 'full' },
  { path: '', redirectTo: '/home', pathMatch: 'full' },
  {
    path: 'users',
    loadComponent: () =>
      import('./modules/users/users.component').then(m => m.UsersComponent),
    canActivate: [AuthGuard, AdminRoleGuard],
  },
  {
    path: 'users/import',
    canActivate: [AuthGuard, AdminRoleGuard],
    canDeactivate: [UnsavedChangesGuard],
    loadComponent: () =>
      import('./modules/users/import/user-import-page.component').then(
        m => m.UserImportPageComponent
      ),
  },
  {
    path: 'users/ranks',
    canActivate: [AuthGuard, AdminRoleGuard],
    loadComponent: () =>
      import('./modules/users/rank-management/rank-management.component').then(
        m => m.RankManagementComponent
      ),
  },
  {
    path: 'users/ranks/:id',
    canActivate: [AuthGuard, AdminRoleGuard],
    canDeactivate: [UnsavedChangesGuard],
    loadComponent: () =>
      import('./modules/users/edit-rank-page/edit-rank-page.component').then(
        m => m.EditRankPageComponent
      ),
  },
  {
    path: 'users/roles',
    canActivate: [AuthGuard, AdminRoleGuard],
    loadComponent: () =>
      import('./modules/users/role-management/role-management.component').then(
        m => m.RoleManagementComponent
      ),
  },
  {
    path: 'users/roles/:id',
    canActivate: [AuthGuard, AdminRoleGuard],
    canDeactivate: [UnsavedChangesGuard],
    loadComponent: () =>
      import('./modules/users/edit-role-page/edit-role-page.component').then(
        m => m.EditRoleComponent
      ),
  },
  {
    path: 'users/logbook-mapping',
    canActivate: [AuthGuard, AdminRoleGuard, LogbookLoginEnabledGuard],
    loadComponent: () =>
      import(
        './modules/users/logbook-mapping/logbook-mapping-page.component'
      ).then(m => m.LogbookMappingPageComponent),
  },
  {
    path: 'users/:id',
    loadComponent: () =>
      import('./modules/users/edit-user-page.component').then(
        m => m.EditUserComponent
      ),
    canActivate: [AuthGuard, AdminRoleGuard],
    canDeactivate: [UnsavedChangesGuard],
  },
];

const adminRoutes: Routes = [
  {
    path: 'admin',
    canActivate: [AuthGuard, AdminEnabledGuard],
    loadComponent: () =>
      import('./admin/components/home/admin-home.component').then(
        m => m.AdminHomeComponent
      ),
  },
  {
    path: 'admin/templates/:templateId',
    canActivate: [AuthGuard, AdminEnabledGuard],
    loadComponent: () =>
      import('./admin/components/editor/admin-editor.component').then(
        m => m.AdminEditorComponent
      ),
  },
];

export const routes: Routes = [...baseRoutes, ...adminRoutes];
